import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Images,
  Video,
  ChevronLeft,
  ChevronRight,
  X,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAsset } from '@/client/utils/assets';

/**
 * CMS media gallery, ported from ff-ui (pages/Content/components/CMSMediaSlider)
 * so the admin content preview renders media exactly like the public page.
 */

interface SliderAsset {
  asid?: number | string;
  mime_type?: string;
  path?: string;
  name?: string;
  public_url?: string;
  public_url_uc?: string;
  asset_type?: { extensions?: string; name?: string } | null;
  AssetType?: { extensions?: string; name?: string } | null;
  [key: string]: unknown;
}

interface CMSMediaSliderProps {
  assets: SliderAsset[];
  title?: string;
}

function isVideo(a: SliderAsset): boolean {
  const mt = a.mime_type?.toLowerCase?.();
  if (mt?.startsWith('video/')) return true;
  const at = (a as any).AssetType ?? a.asset_type;
  const ext = (typeof at?.extensions === 'string' ? at.extensions : '').toLowerCase();
  if (/mp4|webm|ogg|mov/.test(ext)) return true;
  const name = (typeof at?.name === 'string' ? at.name : '').toLowerCase();
  if (name === 'video') return true;
  const path = (a.path || a.name || a.public_url || '').toLowerCase();
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(path);
}

function getImageFallbackChain(asset: SliderAsset): string[] {
  const urls: string[] = [];
  const optimized = getAsset(asset as any, 'large');
  if (optimized) urls.push(optimized);
  if (asset.public_url_uc) urls.push(asset.public_url_uc);
  if (asset.public_url) urls.push(asset.public_url);
  return urls;
}

/**
 * Image with a prioritized URL fallback chain (optimized proxy → direct UC →
 * legacy public_url). Walks the chain on each `onError` and, once every source
 * has failed, swaps in a soft "image unavailable" tile.
 */
const FallbackImg: React.FC<{
  srcs: string[];
  alt: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}> = ({ srcs, alt, className, onClick }) => {
  const [srcIndex, setSrcIndex] = useState(0);
  const [allFailed, setAllFailed] = useState(false);

  const firstSrc = srcs[0];
  useEffect(() => {
    setSrcIndex(0);
    setAllFailed(false);
  }, [firstSrc]);

  if (!srcs.length || allFailed) {
    return (
      <div
        role="img"
        aria-label={alt}
        onClick={onClick}
        className={`${className ?? ''} flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-gray-300 select-none`}
      >
        <Images className="w-1/4 h-1/4 max-w-[64px] max-h-[64px] min-w-[14px] min-h-[14px]" />
      </div>
    );
  }

  return (
    <img
      src={srcs[srcIndex]}
      alt={alt}
      className={className}
      onClick={onClick}
      onError={() => {
        if (srcIndex < srcs.length - 1) {
          setSrcIndex((i) => i + 1);
        } else {
          setAllFailed(true);
        }
      }}
    />
  );
};

const CMSMediaSlider: React.FC<CMSMediaSliderProps> = ({ assets, title = 'Content' }) => {
  const imageAssets = assets.filter((a) => !isVideo(a));
  const videoAssets = assets.filter(isVideo);

  const imageSrcChains = imageAssets.map((img) => getImageFallbackChain(img));
  const images = imageSrcChains.map((chain) => chain[0]).filter((url): url is string => !!url);

  const videos = videoAssets.map((v) => {
    const directUc =
      v.public_url_uc &&
      typeof v.public_url_uc === 'string' &&
      v.public_url_uc.startsWith('https://ucarecdn.com/')
        ? v.public_url_uc
        : undefined;
    const proxyUrl = getAsset(v as any, { type: 'video', videoPreset: '720p', formatPreset: 'mp4_web', redirect: true } as any);
    const posterUrl = getAsset(v as any, { type: 'video', thumbnail: 'poster', redirect: true } as any);
    return {
      url: directUc ?? proxyUrl ?? v.public_url_uc ?? v.public_url ?? '',
      thumbnail: posterUrl ?? images[0] ?? '',
    };
  });

  const hasImages = images.length > 0;
  const hasVideos = videos.length > 0;

  const [currentImage, setCurrentImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'photos' | 'video'>(hasImages ? 'photos' : 'video');
  const [currentVideo, setCurrentVideo] = useState(0);
  const [hoveredGridImage, setHoveredGridImage] = useState<number | null>(null);

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

  const nextImage = useCallback(() => {
    if (selectedTab === 'photos') setCurrentImage((p) => (p + 1) % images.length);
    else setCurrentVideo((p) => (p + 1) % videos.length);
  }, [selectedTab, images.length, videos.length]);

  const prevImage = useCallback(() => {
    if (selectedTab === 'photos') setCurrentImage((p) => (p - 1 + images.length) % images.length);
    else setCurrentVideo((p) => (p - 1 + videos.length) % videos.length);
  }, [selectedTab, images.length, videos.length]);

  const handleTouchStart = (e: React.TouchEvent) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); };
  const handleTouchMove = (e: React.TouchEvent) => { setTouchEnd(e.targetTouches[0].clientX); };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const d = touchStart - touchEnd;
    if (d > 50) nextImage();
    if (d < -50) prevImage();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isFullscreen) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, nextImage, prevImage]);

  useEffect(() => {
    const cb = () => setIsVideoFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', cb);
    return () => document.removeEventListener('fullscreenchange', cb);
  }, []);

  const openModal = (index: number, tab: 'photos' | 'video') => {
    setSelectedTab(tab);
    if (tab === 'photos') setCurrentImage(index);
    else setCurrentVideo(index);
    setIsModalOpen(true);
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying((p) => !p);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const bar = progressBarRef.current;
    const vid = videoRef.current;
    if (!bar || !vid || !Number.isFinite(videoDuration) || videoDuration <= 0) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    vid.currentTime = ratio * videoDuration;
    setVideoTime(vid.currentTime);
  };

  const toggleVideoFS = (e: React.MouseEvent) => {
    e.stopPropagation();
    const el = videoContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.().then(() => setIsVideoFullscreen(true)).catch(() => {});
    else document.exitFullscreen?.().then(() => setIsVideoFullscreen(false)).catch(() => {});
  };

  if (!hasImages && !hasVideos) return null;

  return (
    <div className="w-full relative">
      <div className="w-full flex flex-col gap-2 h-[300px] sm:h-[400px] lg:h-[500px] lg:flex-row p-0 sm:p-3">
        <div className={`w-full h-full relative ${images.length === 2 ? 'lg:w-1/2' : 'lg:w-3/5'}`}>
          <div className="absolute top-4 left-4 z-10 flex gap-2">
            {hasImages && (
              <button
                onClick={() => openModal(0, 'photos')}
                className="px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 bg-white/90 hover:bg-white shadow-lg backdrop-blur-sm transition-colors"
              >
                <Images className="w-4 h-4" />
                Photos ({images.length})
              </button>
            )}
            {hasVideos && (
              <button
                onClick={() => openModal(0, 'video')}
                className="px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 bg-white/90 hover:bg-white shadow-lg backdrop-blur-sm transition-colors"
              >
                <Video className="w-4 h-4" />
                Video
              </button>
            )}
          </div>

          {hasVideos && !hasImages ? (
            <div ref={videoContainerRef} className="w-full h-full cursor-pointer relative rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                src={videos[0].url}
                className="h-full w-full object-cover"
                poster={videos[0].thumbnail}
                preload="metadata"
                controls={false}
                muted={isMuted}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={() => videoRef.current && setVideoTime(videoRef.current.currentTime)}
                onLoadedMetadata={() => videoRef.current && setVideoDuration(videoRef.current.duration)}
              />
              <button onClick={toggleVideoFS} className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full p-2 text-white transition-colors">
                {isVideoFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
              {videoDuration > 0 && (
                <div ref={progressBarRef} onClick={handleProgressClick} className="absolute bottom-0 left-0 right-0 z-10 h-2 bg-white/30 cursor-pointer hover:h-2.5 transition-all">
                  <div className="h-full bg-white rounded-b-md" style={{ width: `${(videoTime / videoDuration) * 100}%` }} />
                </div>
              )}
              <div className="absolute bottom-4 left-4 z-10">
                <button onClick={handlePlayPause} className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg hover:bg-white transition-colors">
                  {isPlaying ? <Pause className="w-6 h-6 text-gray-700" /> : <Play className="w-6 h-6 text-gray-700" />}
                </button>
              </div>
              <div className="absolute bottom-4 right-4 z-10">
                <button
                  onClick={(e) => { e.stopPropagation(); setIsMuted((m) => !m); }}
                  className="bg-white/90 backdrop-blur-sm rounded-full p-2.5 shadow-lg hover:bg-white transition-colors"
                >
                  {isMuted ? <VolumeX className="w-5 h-5 text-gray-700" /> : <Volume2 className="w-5 h-5 text-gray-700" />}
                </button>
              </div>
            </div>
          ) : hasImages ? (
            <div
              className="w-full h-full cursor-pointer relative rounded-lg overflow-hidden"
              onClick={() => openModal(0, 'photos')}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <FallbackImg srcs={imageSrcChains[currentImage] || []} alt={title} className="w-full h-full object-cover rounded-lg" />
              {images.length > 1 && (
                <>
                  <div className="absolute bottom-3 left-3 bg-black/60 text-white px-2 py-1 rounded text-xs">
                    {currentImage + 1} / {images.length}
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="lg:hidden absolute left-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="lg:hidden absolute right-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </motion.button>
                </>
              )}
            </div>
          ) : null}
        </div>

        {hasImages && images.length > 1 && (
          <div className={`hidden lg:block h-full relative ${images.length === 2 ? 'w-1/2' : 'w-2/5'}`}>
            <div className={`grid gap-2 h-full ${
              images.length === 2 ? 'grid-cols-1'
              : images.length <= 4 ? 'grid-cols-2 grid-rows-2'
              : 'grid-cols-2'
            }`}>
              {imageSrcChains.slice(1, 5).map((chain, index) => (
                <div
                  key={index}
                  className={`overflow-hidden rounded-lg cursor-pointer relative ${
                    images.length === 4 && index === 0 ? 'col-span-2' : 'w-full h-full'
                  }`}
                  onClick={() => openModal(index + 1, 'photos')}
                  onMouseEnter={() => setHoveredGridImage(index)}
                  onMouseLeave={() => setHoveredGridImage(null)}
                >
                  <FallbackImg srcs={chain} alt={`${title} ${index + 2}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                  {index === 3 && images.length > 5 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-xl font-semibold">+{images.length - 5} more</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <AnimatePresence>
              {hoveredGridImage !== null && images[hoveredGridImage + 1] && (
                <motion.div
                  key={hoveredGridImage}
                  initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="absolute inset-0 z-20 p-3 pointer-events-none"
                >
                  <div className="w-full h-full rounded-lg overflow-hidden bg-black/90 shadow-2xl flex items-center justify-center">
                    <FallbackImg srcs={imageSrcChains[hoveredGridImage + 1] || []} alt={`${title} preview`} className="max-w-full max-h-full object-contain" />
                    <div className="absolute bottom-5 right-5 bg-black/60 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                      {hoveredGridImage + 2} / {images.length}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className="relative w-full h-full sm:w-[95vw] sm:h-[95vh] sm:max-w-7xl bg-white sm:rounded-lg shadow-2xl flex flex-col"
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-3 sm:p-4 border-b">
                <div className="flex gap-2 sm:gap-4">
                  {hasImages && (
                    <button
                      onClick={() => setSelectedTab('photos')}
                      className={`px-3 py-2 sm:px-4 rounded flex items-center gap-2 text-sm ${selectedTab === 'photos' ? 'bg-indigo-900 text-white' : 'bg-gray-200'}`}
                    >
                      <Images className="w-4 h-4 sm:w-5 sm:h-5" /> Photos
                    </button>
                  )}
                  {hasVideos && (
                    <button
                      onClick={() => setSelectedTab('video')}
                      className={`px-3 py-2 sm:px-4 rounded flex items-center gap-2 text-sm ${selectedTab === 'video' ? 'bg-indigo-900 text-white' : 'bg-gray-200'}`}
                    >
                      <Video className="w-4 h-4 sm:w-5 sm:h-5" /> Video
                    </button>
                  )}
                </div>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-600 hover:text-gray-900 p-1">
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              <div className="flex-grow overflow-auto p-2 sm:p-4">
                {selectedTab === 'photos' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                    {imageSrcChains.map((chain, index) => (
                      <div key={index} className="cursor-pointer hover:opacity-80 transition-opacity" onClick={() => { setCurrentImage(index); setIsFullscreen(true); }}>
                        <FallbackImg srcs={chain} alt={`${title} ${index + 1}`} className="w-full h-48 sm:h-64 object-cover rounded-xl sm:rounded-lg" />
                      </div>
                    ))}
                  </div>
                )}
                {selectedTab === 'video' && hasVideos && (
                  <div className="w-full aspect-video max-w-4xl mx-auto">
                    <video src={videos[currentVideo].url} poster={videos[currentVideo].thumbnail} controls className="w-full h-full rounded-lg" />
                    {videos.length > 1 && (
                      <div className="flex justify-center mt-4 gap-2">
                        {videos.map((_, i) => (
                          <button key={i} onClick={() => setCurrentVideo(i)} className={`w-3 h-3 rounded-full ${currentVideo === i ? 'bg-indigo-900' : 'bg-gray-300'}`} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center"
            onClick={() => setIsFullscreen(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <motion.button
              whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 rounded-full p-2 z-10"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </motion.button>

            <motion.div
              className="relative w-full flex-grow flex items-center justify-center px-4"
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                key={currentImage}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
                className="max-h-[70vh] sm:max-h-[80vh] max-w-full"
              >
                <FallbackImg
                  srcs={imageSrcChains[currentImage] || []}
                  alt={title}
                  className="max-h-[70vh] sm:max-h-[80vh] max-w-full object-contain rounded-xl sm:rounded-lg shadow-2xl"
                />
              </motion.div>
              {images.length > 1 && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="hidden sm:block absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-3"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="hidden sm:block absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-3"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </motion.button>
                </>
              )}
            </motion.div>

            <div className="absolute top-4 left-4 bg-white/10 text-white px-4 py-1.5 rounded-full backdrop-blur-sm text-sm">
              {currentImage + 1} / {images.length}
            </div>

            <div className="sm:hidden absolute bottom-20 left-1/2 -translate-x-1/2 bg-white/10 text-white px-4 py-2 rounded-full backdrop-blur-sm text-xs">
              Swipe to navigate
            </div>

            <div className="w-full max-w-4xl py-2 sm:py-4 flex justify-center items-center space-x-1 sm:space-x-2 overflow-x-auto px-4">
              {imageSrcChains.map((chain, index) => (
                <motion.div
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setCurrentImage(index); }}
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className={`w-12 h-12 sm:w-16 sm:h-16 cursor-pointer rounded-lg overflow-hidden transition-all duration-300 flex-shrink-0 ${
                    currentImage === index ? 'border-2 border-white scale-110' : 'opacity-50 hover:opacity-80'
                  }`}
                >
                  <FallbackImg srcs={chain} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CMSMediaSlider;
